import { useEffect, useRef, useState } from 'react';
import type { LogEntry, LogEvent } from '../api.js';
import { CARD_LABEL_LONG } from '../cardLabels.js';
import { AIM_LABEL } from '../aimLabels.js';

/**
 * What actually happened, in words.
 *
 * The engine emits an event for every rule it applies, and until now they
 * were stored and never shown: a robot healed, was granted an option, took
 * spike damage or archived, and the player saw only the result. Movement
 * you can watch; a rule firing you cannot.
 *
 * The wording below is the only place in the client that interprets a
 * rule, so it is deliberately literal. It says what the engine did, not
 * what it might have meant.
 */

const COMPASS: Record<string, string> = {
  N: 'north', E: 'east', S: 'south', W: 'west',
};

/** What moved the robot, in words. The cause is the difference between
 * "you drove into a pit" and "a conveyor carried you into one", which is
 * exactly what a player needs to know. */
const CAUSE: Record<string, string> = {
  card: '',
  push: 'was pushed',
  conveyor: 'rode a conveyor',
  expressConveyor: 'rode an express conveyor',
  current: 'was carried by a current',
  pusher: 'was shoved by a pusher',
  gear: 'was turned by a gear',
  slide: 'slid',
  ramp: 'took a ramp',
  portal: 'went through a portal',
};

/** Terrain in words, for the lines that have to name which one acted.
 * Only the terrains the log actually mentions are here; anything else
 * falls back to its own key, which is already close to readable. */
const TERRAIN: Record<string, string> = {
  oil: 'oil',
  flamingOil: 'flaming oil',
  mud: 'mud',
  sand: 'sand',
  gravel: 'gravel',
  water: 'water',
  ice: 'ice',
};

const at = (p?: { x: number; y: number }) => (p ? `${p.x},${p.y}` : '?');

/** The engine's phase name in words. It was printed raw, so the heading
 * read "runningRegister" -- and with the replay button immediately after
 * it and no separator, "runningRegisterreplay". */
function phaseLabel(phase: string | null | undefined): string {
  if (!phase) return '';
  return phase
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
}


/** The card that caused this, as "Move 2 (priority 690)". Empty when the
 * event was not caused by a card. */
function cardPrefix(event: LogEvent): string {
  if (!event.card) return '';
  const label = CARD_LABEL_LONG[event.card.type] ?? event.card.type;
  return `${label} (priority ${event.card.priority}) `;
}

/** A missile's place in the turn order, as " (priority 735)". A missile
 * flies between the cards rather than with them, so the number is the
 * only thing that says when -- and it was never shown. */
function missilePriority(event: LogEvent): string {
  return event.priority === undefined ? '' : ` (priority ${event.priority})`;
}

/** One card, as "Move 2 (priority 690)". */
function cardText(card: { type: string; priority: number }): string {
  return `${CARD_LABEL_LONG[card.type] ?? card.type} (priority ${card.priority})`;
}

/**
 * The swap a randomizer, abort or scramble made, as "replaces X with Y".
 *
 * Null when the event carries no drawn card at all, which is the only
 * case with nothing to say. A register that was empty gets "puts Y in":
 * calling that a replacement would invent a card that was never there.
 * Rows logged before the outgoing cards were recorded have no `replaced`
 * and fall back to naming only what went in.
 */
function swapped(event: LogEvent): string | null {
  if (!event.card) return null;
  const drew = cardText(event.card);
  if (!event.replaced) return `draws ${drew}`;
  if (event.replaced.length === 0) return `puts ${drew}`;
  // Two cards in one register is a real shape -- an Option can add a
  // second -- and both of them go.
  const out = event.replaced.map(cardText).join(' and ');
  return `replaces ${out} with ${drew}`;
}

function describeMove(
  event: LogEvent,
  who: string,
  nameOf: (robotId: string) => string,
): string | null {
  const card = cardPrefix(event);

  switch (event.type) {
    // In the switch rather than the table below because the table is
    // handed a count and a name and nothing else, and the SIDE is the
    // point: four shields are out at once, and which one went is what
    // tells the player where the shot came from.
    // The side is named as the ROBOT's, not the compass's: a player reads
    // their own robot, and "your front shield" says where to look in a
    // way that "your south shield" does not. `facing` is the fallback for
    // rows written before the relative side was recorded.
    case 'damageShielded':
      return `${who}'s ${AIM_LABEL[event.side ?? ''] ?? COMPASS[event.facing ?? ''] ?? event.facing} `
        + `${event.name ?? 'shield'} stops ${event.count ?? 1} damage`;

    // WHERE the point came off. A flag repairs by rule and draws no
    // wrench, so "rick2 repaired 1 damage" on a square with nothing on it
    // read like a bug -- the player is standing on the reason and cannot
    // see it. Older rows carry no cause and keep the bare line.
    case 'healed': {
      const where = event.cause === 'flag' ? ' on a flag'
        : event.cause === 'generator' ? ' at a generator'
          : event.cause === 'repairSite' ? ' at a repair site' : '';
      return event.count === undefined
        ? `${who} repaired damage${where}`
        : `${who} repaired ${event.count} damage${where}`;
    }

    // The shooter is the subject: the damage lands on them, and the robot
    // that reflected the beam is named as its source.
    case 'laserReflected':
      return `${nameOf(event.sourceId ?? '')}'s Reflector reflects the laser `
        + `back at ${who} for ${event.count ?? 1} damage`;

    // A missile is the only thing on the board that acts on its own, so
    // it is the subject of its own lines and its owner is possessive.
    // The launch was the only one of these that existed: `advanceMissiles`
    // reported damage and nothing else, so a shell crossing the board,
    // going off against a wall or leaving the course wrote no line at all.
    case 'missileLaunched':
      return `${who} launches a missile${missilePriority(event)}`;

    case 'missileFlew':
      return `${who}'s missile${missilePriority(event)} flies`
        + ` from ${at(event.from)} to ${at(event.to)}`;

    case 'missileLost':
      return `${who}'s missile${missilePriority(event)} flies off the`
        + ' edge of the board';

    case 'missileExploded': {
      const shell = `${who}'s missile${missilePriority(event)}`;
      // The size of the bang, named the way a mine's is: 4 in the square
      // it goes off in, halving outward from there. Without it an
      // explosion that caught nobody was a line with no number in it at
      // all, and one that caught somebody made the reader add up the
      // damage lines underneath to work out how big it had been.
      const bang = `explodes for ${event.count ?? 4} points of damage`;
      if (event.against === 'pusher') {
        return `${shell} is shoved by a pusher and ${bang} at ${at(event.to)}`;
      }
      if (event.against === 'robot') {
        return event.targetId
          ? `${shell} ${bang} on ${nameOf(event.targetId)} at ${at(event.to)}`
          : `${shell} ${bang} on a robot at ${at(event.to)}`;
      }
      if (event.against === 'repulsor') {
        return `${shell} hits a repulsor field at ${at(event.to)} and ${bang}`;
      }
      return `${shell} hits a wall at ${at(event.to)} and ${bang}`;
    }

    // A randomizer names both halves of the swap, because it fires as the
    // register is revealed: the card it replaces is already public and
    // the card it draws is about to be. An abort and a scrambler bomb
    // draw registers that are still secret, so their events carry no card
    // and their lines say only which register was drawn.
    case 'randomized': {
      const swap = swapped(event);
      return swap
        ? `${who} randomizer ${swap} in register ${event.register}`
        : `${who} randomizer replaces register ${event.register}`;
    }

    case 'aborted':
      return `${who} aborts: register ${event.register} drawn at random`;

    // Two rules meet on a flag square: the flag counts only when it is
    // the next one in order, and the wrench every flag carries archives
    // either way. "archived at flag 1" said only the second and read as
    // though the first had happened too, so a robot that landed on a
    // flag it had already tagged looked like it was scoring again.
    //
    // Rows logged before `tagged` was recorded have no answer either way
    // and keep the old wording.
    case 'archivedAtFlag':
      return event.tagged
        ? `${who} archived and tagged at flag ${event.count}`
        : `${who} archived at flag ${event.count}`;

    // Both halves of the trade. The line named only the option that came
    // back, so the log never said what it had cost.
    case 'optionExchanged': {
      if (!event.gaveUp) return null;
      return `${who} exchanges ${event.gaveUp} for ${event.name ?? 'another option'}`;
    }

    case 'scrambledDraw':
      return `${who} draws register ${event.register} at random - scrambled`;

    // Which card the Conditional put into the register. Public now: the
    // register it lands in is being revealed.
    case 'conditionalUsed':
      return event.card
        ? `${who} used conditional: ${CARD_LABEL_LONG[event.card.type] ?? event.card.type}`
          + ` (priority ${event.card.priority})`
        : `${who} used their conditional`;

    // Which Option, not which card: the card goes face down, and this
    // fires as the program is submitted. The Conditional's is named by
    // `conditionalUsed` when it reaches a register.
    case 'cardSetAside':
      return `${who} sets a card aside on ${event.name ?? 'an option'}`;

    case 'optionActivated':
      return `${who} activates ${event.name ?? 'an option'} for this turn`;

    case 'optionAimed':
      return `${who} turns ${event.name ?? 'an option'} to face`
        + ` ${AIM_LABEL[event.side ?? ''] ?? event.side}`;

    // The card itself is named on the lines describing what it did, so
    // this one only reports where the robot started.
    case 'cardExecuted':
      return `${who} plays ${CARD_LABEL_LONG[event.card?.type ?? ''] ?? event.card?.type}`
        + ` (priority ${event.card?.priority}) from ${at(event.from)}`
        + ` facing ${COMPASS[event.facing ?? ''] ?? event.facing}`;

    case 'moved': {
      const how = CAUSE[event.cause ?? 'card'];
      if (!event.from || !event.to) return `${who} ${card}${how || 'moved'}`;
      if (how) return `${who} ${how} from ${at(event.from)} to ${at(event.to)}`;
      return `${who} ${card}moved from ${at(event.from)} to ${at(event.to)}`;
    }

    case 'rotated': {
      const way = event.turn === 'about' ? 'around'
        : event.turn ? event.turn : null;
      const facing = event.facing
        ? ` and faced ${COMPASS[event.facing] ?? event.facing}` : '';

      if (event.cause === 'gear') {
        return `${who} Gear ${way === 'left' ? 'Left' : 'Right'}${facing}`;
      }
      // A curved belt or current turns the robot as it carries it.
      if (event.cause === 'conveyor' || event.cause === 'expressConveyor'
        || event.cause === 'current') {
        const belt = event.cause === 'conveyor' ? 'Conveyor'
          : event.cause === 'expressConveyor' ? 'Express conveyor'
            : 'Current';
        return `${who} ${belt} turned ${way ?? ''}${facing}`.replace('  ', ' ');
      }
      if (card) return `${who} ${card}${facing.replace(' and faced', 'and faced')}`.trim();
      return `${who} turned ${way ?? ''}${facing}`.replace('  ', ' ');
    }

    case 'pushed':
      return event.to ? `${who} was pushed to ${at(event.to)}` : `${who} was pushed`;

    case 'launched':
      return event.to
        ? `${who} ${card}launched off a stunt ramp to ${at(event.to)}`.replace(/\s+/g, ' ')
        : `${who} ${card}launched off a stunt ramp and off the board`.replace(/\s+/g, ' ');

    // Internal: recorded so mines and debris can answer for movement, and
    // never shown. A raw "rick: enteredSquare" in the log is noise.
    case 'enteredSquare':
      return '';

    case 'tokenDropped': {
      const dropped = (event as { kind?: string }).kind;
      const label = dropped
        ? dropped.replace(/([A-Z])/g, ' $1').toLowerCase()
        : 'something';
      return `${who} drops ${label}`;
    }

    case 'returnedToPlay': {
      const e = event as {
        to?: { x: number; y: number }; count?: number; deaths?: number;
      };
      // Rows written before the event carried a position fall back.
      if (!e.to) return `${who} came back at its archive marker`;
      const nth = e.deaths ? ` (death ${e.deaths})` : '';
      const dmg = e.count === 0 ? 'undamaged' : `with ${e.count} damage`;
      return `${who} came back at its archive marker ${at(e.to)} ${dmg}${nth}`;
    }

    case 'beamsCancelled': {
      const e = event as { kind?: string };
      if (e.kind === 'converged') {
        return `${who} is not moved: two robots forced onto one square`;
      }
      if (e.kind === 'deadlocked') {
        return `${who} is not moved: robots forced into each other`;
      }
      return `${who} is not moved: the beams on it cancel out`;
    }

    // The shooters' own lines are in firing order, which is not the order
    // the shoves land in. Without this a wall that stopped the second
    // shove looked like the first one never happened.
    case 'shovesOrdered': {
      const [first, second] = event.order ?? [];
      if (!first || !second) return '';
      const beats = first.priority === second.priority ? 'ties with' : 'beats';
      return `${who} is shoved ${COMPASS[first.facing]} first, then ${COMPASS[second.facing]}: `
        + `${nameOf(first.sourceId)}'s priority ${first.priority} ${beats} `
        + `${nameOf(second.sourceId)}'s ${second.priority}`;
    }

    case 'flagConveyed': {
      const e = event as { count?: number; to?: { x: number; y: number } };
      return `flag ${e.count ?? '?'} is carried to ${at(e.to)}`;
    }

    case 'flagLost': {
      const e = event as { count?: number; cause?: string; x?: number; y?: number };
      const which = `flag ${e.count ?? '?'}`;
      const where = e.x === undefined || e.y === undefined
        ? '' : ` at ${at({ x: e.x, y: e.y })}`;
      // Named by what took it, because a player who cannot see the flag
      // needs to know whether to wait a register or go and look.
      const how = e.cause === 'carriedIntoPit' ? 'a belt carries it into a pit'
        : e.cause === 'carriedOff' ? 'a belt carries it off the board'
          : e.cause === 'trapDoor' ? 'a trap door opens under it'
            : 'a crusher comes down on it';
      return `${which} is destroyed${where}: ${how}`;
    }

    case 'flagReturned': {
      const e = event as { count?: number; x?: number; y?: number };
      const where = e.x === undefined || e.y === undefined
        ? '' : ` at ${at({ x: e.x, y: e.y })}`;
      return `flag ${e.count ?? '?'} reappears${where}`;
    }

    case 'tokenSwallowed': {
      const e = event as { kind?: string; cause?: string; x?: number; y?: number };
      const label = e.kind
        ? e.kind.replace(/([A-Z])/g, ' $1').toLowerCase()
        : 'something';
      // Flat devices ride belts and currents, so a device can now leave
      // the board the same way a robot does.
      if (e.cause === 'carriedOff' || e.cause === 'carriedIntoPit') {
        const where = e.x === undefined || e.y === undefined
          ? '' : ` at ${at({ x: e.x, y: e.y })}`;
        const fate = e.cause === 'carriedIntoPit' ? 'into a pit' : 'off the board';
        return `a belt carries ${label} ${fate}${where}`;
      }
      if (e.cause === 'crushed') {
        const where = e.x === undefined || e.y === undefined
          ? '' : ` at ${at({ x: e.x, y: e.y })}`;
        return `a crusher comes down on ${label}${where}`;
      }
      return `the trap door opens and swallows ${label}`;
    }

    case 'tokenFizzled': {
      const e = event as { kind?: string; reason?: string };
      const label = e.kind
        ? e.kind.replace(/([A-Z])/g, ' $1').toLowerCase()
        : 'something';
      switch (e.reason) {
        case 'empty':
          return `${who} has no ${label} left to drop`;
        // The one liquid refusal that really is a dispersal: the drop
        // happened and came to nothing. The shot IS spent, so the log has
        // to account for it. Every other liquid refusal below is a square
        // that never took the liquid at all.
        case 'disperses':
          return `${who} drops ${label} in water, which disperses to no effect`;
        // Not open floor or open terrain -- a wrench, a gear, a pusher.
        // Nothing was poured and the shot is not spent, which is exactly
        // why this stopped being reported as a dispersal.
        case 'notOpenFloor':
          return `${who} cannot place ${label} there, the square is not open floor`;
        case 'alreadyThere':
          return `${who} drops ${label}, but there is already ${label} here`;
        case 'blocked':
          return `${who} cannot place ${label} there, blocked by a wall or repulsor field`;
        case 'onFlag':
          return `${who} cannot place ${label} on a flag`;
        // A bridge spans a pit and nothing else. Named rather than folded
        // into `blocked`, which says "a wall or repulsor field" and would
        // send the player looking for a wall that is not there.
        case 'notAPit':
          return `${who} cannot place ${label} there, a bridge only spans a pit`;
        // Goo's own placement rule. Each names the square, because the
        // player has to be told the truth about why the shot did not
        // happen -- and because the shot is not spent, the line is the
        // only sign anything was attempted.
        case 'virtual':
          return `${who} is virtual and cannot deploy ${label}`;
        case 'onPit':
          return `${who} cannot place ${label} on a pit`;
        case 'onBelt':
          return `${who} cannot place ${label} on a conveyor belt`;
        case 'onCurrent':
          return `${who} cannot place ${label} on a current`;
        case 'onWater':
          return `${who} cannot place ${label} in water`;
        case 'onTeleporter':
          return `${who} cannot place ${label} on a teleporter`;
        // Void is not a square with something wrong with it -- there is
        // no floor there for anything to sit on. This is also what stops
        // a Bridge Layer making walkable ground out of nothing.
        case 'offCourse':
          return `${who} cannot place ${label} there, it is not part of the course`;
        default:
          return `${who} fails to drop ${label}`;
      }
    }

    // A jump touches nothing in between, so the two ends do not say how
    // far it went -- and how far is the whole point of a teleporter: the
    // card's squares plus 2. The line said neither the distance nor which
    // card bought it.
    case 'teleported': {
      if (!event.to) return `${who} was teleported`;
      const plays = event.card ? `plays ${cardText(event.card)} and ` : '';
      const far = event.count === undefined
        ? '' : ` ${event.count} ${event.count === 1 ? 'square' : 'squares'}`;
      return `${who} ${plays}teleports${far} to ${at(event.to)}`;
    }

    case 'portaled':
      return event.from && event.to
        ? `${who} went through a portal from ${at(event.from)} to ${at(event.to)}`
        : `${who} went through a portal`;

    case 'repulsed':
      return `${who} ${card}hit a repulsor field and was thrown back`
        .replace(/\s+/g, ' ');

    // "If this would result in them landing on a square occupied by
    // another robot, they land in the next open square determined by
    // their direction of movement." It happened in silence, so the
    // landing square did not match the ledge the robot came off.
    case 'fellPast': {
      const over = event.count ?? 1;
      const past = over === 1 && event.targetId
        ? nameOf(event.targetId)
        : `${over} robot${over === 1 ? '' : 's'}`;
      return event.to
        ? `${who} falls past ${past} and lands at ${at(event.to)}`
        : `${who} falls past ${past}`;
    }

    // And the other end of that rule: a robot below and a wall beyond it
    // leave the fall nowhere to come down, so it goes back onto the ledge.
    case 'bouncedBack':
      return event.targetId
        ? `${who} falls onto ${nameOf(event.targetId)}, finds a wall beyond, `
          + 'and bounces back onto the ledge'
        : `${who} finds a wall beyond the drop and bounces back onto the ledge`;

    // The starting square swallowed the whole card. Named terrain by
    // terrain, because the three rules are different -- oil takes the
    // first square of any move, mud stops a Move 2 or 3, sand stops a
    // Move 3 -- and a player who is told which one it was can see why the
    // card they played was the wrong one.
    case 'movementNegated':
      return `${who} ${card}had its movement taken by the `
        + `${TERRAIN[event.terrain ?? ''] ?? event.terrain} and did not move`;

    case 'blocked':
      return `${who} ${card}was blocked and did not move`;

    default:
      return null;
  }
}

/**
 * How each event reads.
 *
 * `name` carries a card or option name where the event has one — an
 * option granted, an option used — so the log can say which rather than
 * "an option card".
 */
const DESCRIBE: Record<
  string,
  (robot: string, count?: number, name?: string, by?: string) => string
> = {
  // Deal
  dealt: (r, n) => `${r} was dealt ${n} card${n === 1 ? '' : 's'}`,
  poweredDown: (r) => `${r} powers down and discards all damage`,
  poweredUp: (r) => `${r} powered back up`,
  lockedRegisterFilled: (r, n) => `${r}'s locked register ${n} was filled at random`,
  // Zero is the start track: nobody at the table had tagged a flag yet,
  // so there was no flag to come in level with.
  joined: (r, n) => (n
    ? `${r} joined the game at flag ${n}`
    : `${r} joined the game on the start track`),

  // Program
  programmed: (r) => `${r} locked in a program`,
  autoFilled: (r, n) => `${r}'s register ${n} was filled at random when the timer ran out`,
  leftoversDiscarded: (r, n) => `${r} discarded ${n} unused card${n === 1 ? '' : 's'}`,
  recompiled: (r, n) => `${r} recompiles: a new hand of ${n} card${n === 1 ? '' : 's'}`,
  recompileDamage: (r) => `${r} takes 1 damage for the recompile`,
  facingSet: (r) => `${r} chose its starting facing`,
  announcedPowerDown: (r) => `${r} announced power down for next turn`,
  registersWiped: (r) => `${r}'s registers were cleared`,
  // randomized, aborted and scrambledDraw are in the switch above, which
  // can read the register they name.
  optionUsed: (r, n, name) => `${r} uses ${name ?? 'an option'}`,
  scramblerBombExploded: (_r, n) => (
    n === 0
      ? 'a scrambler bomb explodes, catching no one'
      : `a scrambler bomb explodes, catching ${n} robot${n === 1 ? '' : 's'}`
  ),
  scrambledByBomb: (r) => `${r} is caught in a scrambler bomb`,
  // The count is what is LEFT of the patch, which is what a player wants
  // to know and what the board draws on the token. "Squares to struggle"
  // named the mechanism instead, and left it unclear whether the number
  // was a distance, a cost, or squares already spent.
  stuckInGoo: (r, n) => `${r} is stuck in goo - ${n ?? 1} goo remaining`,
  brokeFreeOfGoo: (r) => `${r} struggles free of the goo, destroying it`,

  // Terrain and damage
  spikeDamage: (r) => `${r} takes 1 damage from a spiked wall`,
  spikeFloorDamage: (r) => `${r} takes 1 damage from spikes`,
  speedBumpDamage: (r) => `${r} takes 1 damage from a speed bump`,
  // The number matters: a fall onto ground is 2 and a fall into water is
  // 1, and without it the two lines read identically.
  fallDamage: (r, n) => `${r} falls and takes ${n ?? 2} damage`,
  enteredSand: (r) => `${r} stops in sand`,
  waterNegation: (r) => `${r} loses 1 movement in water`,

  // Destruction
  destroyedByDamage: (r) => `${r} was destroyed by damage`,
  destroyedPit: (r) => `${r} fell into a pit`,
  destroyedOffBoard: (r) => `${r} went off the edge of the board`,
  crusherDestroyed: (r) => `${r} is destroyed by a crusher`,
  trapDoorDestroyed: (r) => `${r} fell in as the trap door opened underneath it`,
  // The detailed form is in the switch above, which can read `kind`.

  // Lasers
  boardLaserHit: (r, n) => (
    n && n > 1
      ? `${r} was hit by a board laser for ${n} damage`
      : `${r} was hit by a board laser`
  ),
  beamPushed: (r) => `${r} is pushed 1 square by a pressor beam`,
  beamPulled: (r) => `${r} is pulled 1 square by a tractor beam`,
  bigGunHit: (r, n) => `${r} takes ${n ?? 2} damage from a big gun`,
  bigGunRecoil: (r) => `${r} is shoved back 1 square by the recoil`,
  // Not shown: a weapon that hits nothing is not worth a line.
  weaponMissed: () => '',
  ammoReloaded: (r, n, name) => `${r} reloads ${name ?? 'a weapon'} at a chop shop`,
  // The missile lines are in the switch above, which can read the
  // priority the shell will fly at.
  // Named, because 4 into the square and less further out is worth
  // seeing rather than inferring from the damage lines.
  mineExploded: (_r, n) => `a mine explodes for ${n ?? 4} points of damage`,
  mineDamage: (r, n) => `${r} takes ${n ?? 4} damage from a mine`,
  debrisDamage: (r) => `${r} takes 1 damage crossing debris`,
  // The other half of that: the rule spends debris on the damage it
  // deals. It names no robot because the line above it just did.
  debrisSpent: () => 'that debris is cleared away',
  // An empty robot is debris a seismic charge left, which belongs to
  // nobody -- the same shape `mineExploded` uses for an unowned mine.
  debrisLaid: (r) => (r ? `${r} leaves debris behind` : 'the blast leaves debris behind'),
  seismicDamage: (r, n) => `${r} takes ${n ?? 3} damage from a seismic charge`,
  missileHit: (r, n) => `${r} takes ${n ?? 4} damage from a missile`,

  optionLost: (r, _n, name) => `${r} gave up ${name ?? 'an option'}`,
  archivedUndamaged: (r) => `${r} returns undamaged from a superior archive copy`,
  damageAbsorbed: (r, n) => `${r}'s Ablative Coat absorbs ${n ?? 1} damage`,
  // Named from the event rather than hardcoded: the card that softened
  // the fall is the one the player wants to see credited. It is pushed
  // even when the fall is cancelled outright, where no damage line
  // follows it and this is the only report of what happened.
  damageReduced: (r, n, name) => (
    `${r}'s ${name ?? 'Suspension'} takes ${n ?? 1} off the fall`
  ),
  optionSpent: (r, _n, name) => `${r}'s ${name ?? 'option'} is used up and discarded`,
  optionDestroyed: (r, _n, name, by) => (
    by
      ? `${by}'s Fire Control destroys ${r}'s ${name ?? 'option'}`
      : `${r}'s ${name ?? 'option'} is destroyed`
  ),
  payloadExploded: (r, n, name) =>
    `${r} takes ${n ?? 1} damage from what was left in a ${name ?? 'launcher'}`,
  // Follows the shot that carried it, and says so: Fire Control spends a
  // point of THAT damage to choose which register locks, rather than
  // locking one on top of it. Without "with that damage" the pair read as
  // a lock and then a separate two points.
  // The other half of the pair. A heal only takes a point off; which
  // register that frees is decided by what the robot was holding, so it
  // is worth saying out loud beside whatever did the healing.
  registerUnlocked: (r, n) => `${r}'s register ${n} is unlocked`,
  registerLocked: (r, n, _name, by) => (
    by
      ? `${by}'s Fire Control locks ${r}'s register ${n} with that damage`
      : `${r}'s register ${n} is locked`
  ),
  scrambled: (r, n) => `${r} is scrambled: register ${n} replaced at random`,
  radioControlled: (r) => `${r} is taken over by a radio control beam`,
  // These two name the shooter and always give the number. They used to
  // hide the count when it was 1, so the commonest laser line in the game
  // said only that a robot had been shot -- no number, no shooter.
  rearLaserHit: (r, n, name, by) => (
    by
      ? `${r} takes ${n ?? 1} damage from ${by}'s ${name ?? 'rear laser'}`
      : `${r} takes ${n ?? 1} damage from a rear laser`
  ),
  // The weapon by name when the shooter's laser is not the standard one:
  // "was shot by rick's laser for 2" named the wrong gun for the number
  // beside it.
  robotLaserHit: (r, n, name, by) => (
    by
      ? `${r} was shot by ${by}'s ${name ?? 'laser'} for ${n ?? 1} damage`
      : `${r} was shot by another robot for ${n ?? 1} damage`
  ),
  flamerDamage: (r) => `${r} takes 1 damage from a flamethrower`,
  flamerBurn: (r) => `${r} takes 1 damage entering a flamethrower`,
  flamingOilDamage: (r) => `${r} takes 1 damage from flaming oil`,
  flamingOilBurn: (r) => `${r} takes 1 damage entering flaming oil`,
  rampLandingDamage: (r, n) => `${r} takes ${n ?? 2} damage landing`,
  radioactiveWasteDamage: (r) => `${r} takes 1 damage from radioactive waste`,

  // Checkpoints
  // Not shown: an in-order flag touch always archives, and "archived and
  // tagged at flag 3" says both. A landing that did NOT count is reported
  // by flagOutOfOrder instead.
  flagTouched: () => '',
  flagOutOfOrder: (r, n) =>
    `${r} landed on flag ${n} out of order, so it did not count`,
  repairSiteTouched: (r) => `${r} archived at a repair site`,
  restStop: (r, n) => `${r} repairs ${n ?? 1} damage on a rest stop`,
  sittingOutPitStop: (r) => `${r} sits this register out after its pit stop`,
  rammingGearHit: (r) => `${r} is rammed for 1 damage`,
  spikedWheelsHit: (r, n, _name, by) => (
    by ? `${r} is caught by ${by}'s spiked wheels for ${n ?? 1} damage`
      : `${r} is caught by spiked wheels for ${n ?? 1} damage`
  ),
  miniHowitzerHit: (r, n, _name, by) => (
    by ? `${r} is shelled by ${by}'s mini-howitzer for ${n ?? 1} damage`
      : `${r} is shelled by a mini-howitzer for ${n ?? 1} damage`
  ),
  bumpersHeld: (r) => `${r}'s Hydraulic Bumpers reduce the push by 1 square`,
  rampPushHeld: (r, n) => (
    `${r} is shoved against a ramp and does not move` + (n ? ` - ${n} more square${n === 1 ? '' : 's'} of push would get it up` : '')
  ),
  pitStop: (r, n) =>
    `${r} repairs 1 damage on a pit stop, and is inactive for register ${n}`,
  chopShopVisited: () => '',
  chopShopEmpty: (r) => `${r} stops at a chop shop, which has nothing for it`,
  chopShopDefaulted: (r) => `${r} made no choice at the chop shop, so took an option`,
  optionExchanged: (r, n, name) => `${r} exchanges an option for ${name ?? 'another'}`,
  radioactiveWasteDrawn: (r, n, name) => (name
    ? `${r} drew an option from radioactive waste: ${name}`
    : `${r} drew from radioactive waste`),
  victory: (r) => `${r} touched the last flag and won`,

  // End of turn
  radiationDamage: (r) => `${r} takes 1 damage from radiation`,
  optionGranted: (r, n, name) => `${r} was granted an option: ${name ?? 'a card'}`,
  // Says only why no card came. What happened instead is the neighbouring
  // event's job -- the `healed` a repair site pairs this with, and the
  // damage radioactive waste has already dealt. Both repair sites and the
  // waste use it, and only the two-wrench site gave anything up for it.
  optionSupplyEmpty: (r) => (
    `${r} was owed an option, but every one is already in play`
  ),
  continuingPowerDown: (r) => `${r} stayed powered down`,
  // The detailed form is in the switch above, which can read `to`.
  becameVirtual: (r) => `${r} returns in virtual mode`,
  becameReal: (r) => `${r} leaves virtual mode`,
  announcedPowerDownOnReturn: (r) => `${r} will return powered down`,
  lifeLost: (r) => `${r} spent a life token`,
  eliminated: (r) => `${r} is out of the game`,
  circuitBreakerTripped: (r) => `${r}'s Circuit Breaker forces it to power down next turn`,
};

function describe(event: LogEvent, nameOf: (robotId: string) => string): string {
  // The player's name, resolved once and used by every describer below.
  const who = nameOf(event.robotId);

  const detailed = describeMove(event, who, nameOf);
  // `null` means "no special wording, try the table below"; an empty
  // string means "deliberately not shown". Treating both as falsy printed
  // the raw event type for every event meant to be hidden.
  if (detailed !== null) return detailed;

  const shape = DESCRIBE[event.type];
  // An unrecognised event is shown raw rather than swallowed: a silent
  // gap here would hide a rule firing, which is the whole point of the
  // panel.
  // `count` OR `register`: several events carry the register number
  // instead of a count, and the messages that name a register were being
  // handed `count` — which those events do not have. That is where
  // "draws register undefined at random" came from.
  const n = event.count ?? (event as { register?: number }).register;
  // Who caused it, where the event names someone other than the victim.
  const by = event.sourceId ? nameOf(event.sourceId) : undefined;
  return shape ? shape(who, n, event.name, by) : `${who}: ${event.type}`;
}

export function LogPanel({
  entries, robotIds, youAre, nameOf, onReplay, replayingSeq, onReplayTurn, replayingTurn,
}: {
  /** Supplied by the caller: the log is fetched once and shared with the
   * playback, rather than each fetching it. */
  entries: LogEntry[];
  /** Every robot in the game, so the log can be narrowed to one. */
  robotIds: string[];
  youAre: string | null;
  /** What to call each robot: the player's name, not the internal id. */
  nameOf: (robotId: string) => string;
  /** Replays a register on the board. Absent means replay is unavailable. */
  onReplay?: (entry: LogEntry) => void;
  /** The entry currently being replayed, if any. */
  replayingSeq?: number | null;
  /** Replays every register of a turn. Absent means unavailable. */
  onReplayTurn?: (turn: number) => void;
  /** The turn currently being replayed whole, if any. */
  replayingTurn?: number | null;
}) {
  const [open, setOpen] = useState(true);
  /** null shows everything; otherwise only this robot's events. */
  const [only, setOnly] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, open]);

  // The endpoint returns newest first, which made a turn read backwards:
  // its end before its beginning. A game log is a narrative, so it is
  // reversed here and the view scrolls to the newest at the bottom.
  // Filtering drops the events, then any entry left with none: an empty
  // heading with nothing under it is worse than no heading.
  const withEvents = entries
    .map((entry) => (
      only
        ? { ...entry, events: entry.events.filter((e) => e.robotId === only) }
        : entry
    ))
    .filter((e) => e.events.length > 0)
    .slice()
    .reverse();

  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <div className="row" style={{ gap: '0.4rem' }}>
        <button
          className="secondary log-toggle"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? 'Hide the log' : 'Show the log'}
        </button>

        {open && robotIds.length > 1 && (
          <select
            className="mono log-filter"
            value={only ?? ''}
            onChange={(e) => setOnly(e.target.value || null)}
            aria-label="Show log entries for"
          >
            <option value="">Everyone</option>
            {robotIds.map((robotId) => (
              <option key={robotId} value={robotId}>
                {nameOf(robotId)}{robotId === youAre ? ' (you)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {open && (
        <div className="log" role="log" ref={scroller}>
          {withEvents.length === 0 && (
            <p className="muted">
              {only ? `Nothing recorded for ${nameOf(only)}.` : 'Nothing has happened yet.'}
            </p>
          )}
          {withEvents.map((entry, entryIndex) => {
            // One register is several advances -- each robot's card is
            // its own entry, and laser fire another -- so heading every
            // entry printed "turn 2 / register 3 / running register"
            // three times in a row. Shown only when it changes. The
            // replay button is per ENTRY and stays on every one, since
            // each advance is separately replayable.
            const previous = withEvents[entryIndex - 1];
            const sameHeading = previous
              && previous.turn === entry.turn
              && previous.register === entry.register
              && (previous.phase ?? previous.action) === (entry.phase ?? entry.action);

            return (
            <div key={entry.seq} className="log-entry">
              <p className="log-head mono">
                {!sameHeading && (
                  <>
                    {entry.turn !== null && <>turn <span className="num">{entry.turn}</span></>}
                    {entry.register !== null && (
                      <> / register <span className="num">{entry.register}</span></>
                    )}
                    {' / '}
                    {phaseLabel(entry.phase ?? entry.action)}
                  </>
                )}

                {/* Only entries with a stored frame can be replayed: the
                    events alone cannot say where a robot that did not
                    move was standing. */}
                {onReplay && entry.frame && entry.register !== null && (
                  <>
                    {' '}
                    <button
                      className="log-replay"
                      onClick={() => onReplay(entry)}
                      disabled={replayingSeq === entry.seq}
                    >
                      {replayingSeq === entry.seq ? 'replaying' : 'replay'}
                    </button>
                    {onReplayTurn && entry.turn !== null && (
                      <>
                        {' '}
                        <button
                          className="log-replay"
                          onClick={() => onReplayTurn(entry.turn!)}
                          disabled={replayingTurn === entry.turn}
                        >
                          {replayingTurn === entry.turn ? 'replaying turn' : 'replay turn'}
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
              {entry.events.map((event, i) => {
                const line = describe(event, nameOf);
                if (!line) return null; // deliberately not shown
                return <p key={i} className="log-line">{line}</p>;
              })}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
